import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/qualifications/business-manner/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   共通セクション骨格（全異型・装飾ディバイダー/素hr/❦/紋章 不使用）:
//     Hero(全幅ビルボード) → I.資格概要(リード3段落+細罫データ行+特徴3枚) → II.カリキュラム(番号2カラム編集ブロック)
//     → III.こんな方におすすめ(点検リスト) → IV.教材(番号カード) → V.認定試験(紺罫定義リスト)
//     → VI.料金とお申込み(大字serif受講料+含まれるもの) → VII.FAQ(アコーディオン) → VIII.関連資格+締めCTA。
//   実文出典（創作禁止）: 現行working tree実文（本ページ旧実装の逐語）。数値・受講料・章立て・FAQは改変せず継承。
// tea-sommelier = Heritage型: hero型A(左寄せ全幅) + 紺パネル引用(Section I–II間) + III=cream明色版（紺重複回避）。

export default function TeaSommelierButlerPage() {
  useEffect(() => {
    updateSEO({
      title: '執事資格 Tea Sommelier | 執事検定専門認定 - 全日本執事協会',
      description: 'Tea Sommelier執事資格は、日本茶・中国茶・紅茶の専門知識を持つ執事を認定する執事検定です。茶葉の選定、適切な淹れ方、茶器の扱いなど、お茶に関する高度な執事資格として認められています。',
      keywords: 'Tea Sommelier,執事資格,執事検定,紅茶,専門資格,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/tea-sommelier'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Tea Sommelier' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-book-line', title: '執事の伝統', desc: '歴史から学ぶ紅茶の価値。17世紀の茶葉管理の伝統を理解し、執事が紅茶のスペシャリストである理由を学びます。' },
    { icon: 'ri-cup-line', title: '実践的技術', desc: '正しい淹れ方とサービス。ゴールデンルールに基づいた淹れ方と、執事流の美しいティーサービスを習得します。' },
    { icon: 'ri-global-line', title: '体系的知識', desc: '産地から文化まで網羅。世界の主要産地の特徴やペアリング、ティーウェアの知識まで幅広く学びます。' },
  ];

  const curriculum = [
    { no: '01', title: '執事と紅茶の深い関わり', desc: '17世紀の茶の伝来と執事の役割、英国貴族の館での茶の管理、ティーキャディと執事の責任、茶葉が贅沢品だった時代、執事が紅茶のスペシャリストである理由を学びます。歴史を知ることで、なぜ執事が紅茶の専門家として位置づけられてきたのかが理解できます。' },
    { no: '02', title: '紅茶の基礎知識', desc: '紅茶の製造工程と発酵の仕組み、発酵度による分類（緑茶・烏龍茶・紅茶）、グレードの見方と意味、茶葉の適切な保管方法を習得します。紅茶の基本的な知識を身につけることで、より深い理解が可能になります。' },
    { no: '03', title: '世界の紅茶産地', desc: 'インド（ダージリン・アッサム・ニルギリ）の特徴、スリランカ（セイロン）の各地域、中国紅茶の歴史と種類、その他の産地と特徴を学びます。各産地の気候や土壌が紅茶の味わいに与える影響を理解し、産地別の特徴を習得します。' },
    { no: '04', title: '紅茶の淹れ方（実践）', desc: '執事流の美しい所作、ゴールデンルールの実践方法、茶器の温め方と重要性、蒸らし時間の見極め方を習得します。理論だけでなく、実際に美味しい紅茶を淹れるための技術を身につけます。' },
    { no: '05', title: 'ブレンドティーとフレーバードティー', desc: '代表的なブレンド（アールグレイ、イングリッシュブレックファストなど）、フレーバードティーの世界と製法、茶葉のブレンド技術、オリジナルブレンドの作り方を学びます。ブレンドの奥深さと創造性を理解します。' },
    { no: '06', title: 'ティーペアリング', desc: '食事と紅茶の組み合わせ原則、お菓子と紅茶の相性、時間帯別の紅茶選び、シーン別の提案方法を習得します。紅茶をより楽しむための組み合わせの技術を学びます。' },
    { no: '07', title: '美しいティーサービス', desc: '執事のティーサービス実技、ティーポットの正しい扱い方、注ぎ方の美学と技術、ミルク・砂糖の提供マナーを学びます。見た目にも美しく、心のこもったサービスの技術を習得します。' },
    { no: '08', title: 'ティーウェアの知識', desc: 'ティーカップとソーサーの種類、ティーポットの選び方と特徴、茶こしとストレーナーの使い分け、アンティークティーウェアの魅力を習得します。道具への理解を深めることで、より良いティータイムを演出できます。' },
    { no: '09', title: '世界のお茶（紅茶以外）', desc: '緑茶・烏龍茶の基礎知識、ハーブティーの種類と効能、日本茶の基礎と淹れ方、各国のお茶文化の違いを学びます。紅茶以外のお茶についても理解を深め、幅広い知識を身につけます。' },
    { no: '10', title: '総合演習', desc: 'ティーテイスティング実践、シチュエーション別紅茶選びの演習、確認テストで学習内容を統合します。これまで学んだ知識を実践的に活用し、総合的な理解を深めます。' },
  ];

  const recommend = [
    '紅茶が好きで知識を深めたい方', 'カフェ・紅茶専門店で働く方', 'ホテルでのティーサービスを向上させたい方',
    '自宅で本格的な紅茶を楽しみたい方', '紅茶教室を開きたい方', '英国文化に興味がある方',
    'ティーアドバイザーを目指す方', '執事文化と紅茶の関係を学びたい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約80ページ）', desc: '紅茶の専門知識を詳しく解説した専用テキスト。イラストや図解も豊富で、初めての方でも理解しやすい構成です。' },
    { no: '2', title: 'オンライン動画レッスン', desc: '紅茶の淹れ方やサービス実技を含む動画解説。視聴期間1年間。実際の技術を動画で確認できます。' },
    { no: '3', title: '紅茶産地マップ', desc: '世界の主要産地を視覚的に理解できる参考資料。産地の特徴を地図で確認できます。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験とテイスティングレポート課題を含む試験問題。自宅で取り組める内容です。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式）+ テイスティングレポート' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料8,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約80ページ）', 'オンライン動画レッスン（1年間視聴可能）', '紅茶産地マップ',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/afternoon-tea', name: 'Afternoon Tea', ja: '英国式アフタヌーンティーの専門家', icon: 'ri-cake-3-line' },
    { href: '/qualifications/wine-steward', name: 'Wine Steward', ja: 'ワインサービスの専門家', icon: 'ri-goblet-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: 'すべての専門資格を確認', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: '紅茶初心者でも取得できますか？', a: 'はい、基礎から学べる内容です。紅茶の知識がゼロでも段階的に理解できるカリキュラムです。' },
    { q: 'Afternoon Tea Butler資格との違いは？', a: 'Afternoon Tea Butlerはティータイムの文化とマナー、本資格は紅茶そのものの専門知識に特化しています。' },
    { q: '実際に紅茶を飲む必要がありますか？', a: 'テイスティングレポートは学習内容に基づいた記述課題で、実際の飲用は任意です。' },
    { q: '執事とティーキャディの関係を学べますか？', a: 'はい、第1章で17世紀の茶葉管理の歴史を詳しく学びます。' },
    { q: 'ダージリンとアッサムの違いを学べますか？', a: 'はい、第3章で世界の産地ごとの特徴を詳しく学びます。' },
    { q: 'ゴールデンルールとは何ですか？', a: '美味しい紅茶を淹れるための基本原則です。第4章で詳しく学びます。' },
    { q: 'アールグレイについて詳しく学べますか？', a: 'はい、第5章でアールグレイを含む代表的なブレンドティーを学びます。' },
    { q: '紅茶と食事の組み合わせも学べますか？', a: 'はい、第6章でティーペアリングの原則と実践方法を学びます。' },
    { q: 'ティーポットの選び方も学べますか？', a: 'はい、第8章でティーウェアの知識を詳しく学びます。' },
    { q: '日本茶についても学べますか？', a: 'はい、第9章で日本茶の基礎知識と淹れ方を学びます。' },
    { q: 'カフェで働いていますが役立ちますか？', a: 'はい、紅茶の専門知識とサービス技術の向上に直結する内容です。' },
    { q: '自分でブレンドティーを作れるようになりますか？', a: '第5章でブレンドの基本を学びますが、実際の茶葉は別途ご用意ください。' },
    { q: 'ハーブティーについても学べますか？', a: 'はい、第9章でハーブティーの基礎知識を学びます。' },
    { q: '紅茶の保管方法も学べますか？', a: 'はい、第2章で茶葉の適切な保管方法と注意点を学びます。' },
    { q: '執事流のサービスとは何ですか？', a: '美しい所作と細やかな気配りを重視したティーサービスです。第7章で詳しく学びます。' },
  ];

  return (
    <Layout>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-024-qual-teasom-w2.jpg"
          alt="Tea Sommelier Butler研修風景。執事が紅茶の淹れ方とティーサービスを実践する様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-3">Tea Sommelier</h1>
            <p className="font-['Noto_Serif_JP',serif] text-[#d7b779] text-base sm:text-lg tracking-[0.12em] mb-6">ティー・ソムリエ</p>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              全日本執事協会が認定する、紅茶の専門知識を執事の視点から学ぶ専門資格です。17世紀以来、執事が担ってきた茶葉管理の伝統から、世界の産地・淹れ方・ペアリング・ティーサービスまで、紅茶に関する総合的な知識を体系的に習得します。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3段落+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              紅茶の世界を、<span className="text-[#a38e65]">執事の視点</span>で
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Tea Sommelier Butler資格では、17世紀に東インド会社が英国に茶を伝えて以来、執事が担ってきた茶葉管理の伝統を学びます。当時、茶は非常に高価で貴重な品であり、執事は鍵付きのティーキャディ（茶箱）で厳重に管理していました。この歴史的背景を理解することで、紅茶への理解がより深まります。
              </p>
              <p>
                紅茶の製造工程と発酵度による分類、世界の主要産地（ダージリン、アッサム、セイロンなど）の特徴、ゴールデンルールに基づいた正しい淹れ方、ブレンドティーとフレーバードティーの知識、食事や菓子とのペアリング、執事流の美しいティーサービス、ティーウェアの知識まで、紅茶に関する総合的な専門知識を体系的に習得します。
              </p>
              <p>
                紅茶愛好家としての知識を深めたい方、カフェやホテルでのサービス向上を目指す方に最適です。執事の伝統に根ざした本格的な紅茶の世界を学ぶことで、より深い理解と実践的なスキルが身につきます。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">38,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1–3<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験・資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">自宅受験・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は知識習得を目的とした認定制度です。資格取得により特定の職業への就職や収入を保証するものではありません。全日本執事協会が認定する民間資格です。
              </p>
            </div>
          </section>

          {/* ===== Heritage(紺パネル引用)= about/overview S3 由来。tea-sommelier の個性センターピース ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">— Heritage</span>
            <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14">
              <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
              <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.7] tracking-[0.02em] max-w-3xl">
                17世紀、茶は非常に高価な品でした。執事は鍵付きのティーキャディで茶葉を厳重に管理し、紅茶のスペシャリストとしての役割を担ってきたのです。
              </blockquote>
              <div className="w-11 h-px bg-[#a38e65] mt-8"></div>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              紅茶の歴史から産地、淹れ方、サービスまでを、10の分野で体系的に学びます。執事の伝統に根ざした本格的な紅茶の世界を、段階的に習得します。
            </p>
            <img
              src="/images/generated/jba-fs-177-qualifications_tea-sommelier-v5.jpg"
              alt="Tea Sommelier学習カリキュラムの教材。世界の紅茶産地や淹れ方を解説するテキストと資料"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（Heritage=明色cream版。紺重複回避） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/25 px-6 sm:px-10 lg:px-12 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-[#e2ddd2] pb-4">
                    <i className="ri-check-line text-[#a38e65] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-178-qualifications_tea-sommelier-v5.jpg"
                alt="Tea Sommelier教材一式。テキスト・動画レッスン・紅茶産地マップがそろった学習セット"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）。由来: about/overview S2 の border-t-2 定義リスト文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              テイスティングレポートは実際に紅茶を飲む必要はなく、学習内容に基づいた記述課題です。試験は自宅で行う通信試験です。教材が届いたら、ご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              紅茶の専門知識を体系的に学んで、Tea Sommelier Butlerとしての知識と技術を身につけてください。
            </p>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">38,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">1〜3ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Tea Sommelier Butler資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Tea Sommelier Butler資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
